import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreVertical } from 'lucide-react';
import type { TenantMembership } from '@orqforge/shared';
import {
  apiClient,
  type AccessGroup,
  type CreateIdentityProviderDto,
  type IdentityProvider,
  type IdpProviderType,
} from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { Input } from '../components/ui/Input';

interface MemberWithName extends TenantMembership {
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  displayName?: string;
  identityProvider?: string;
}

const selectClass =
  'h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

function errText(err: unknown, fallback: string): string {
  if (!(err instanceof Error) || !err.message) return fallback;
  try {
    const parsed = JSON.parse(err.message) as { message?: unknown };
    if (typeof parsed.message === 'string' && parsed.message) return parsed.message;
    if (Array.isArray(parsed.message)) return parsed.message.map(String).join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function usable(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? '';
  if (!trimmed || UUID_RE.test(trimmed)) return '';
  return trimmed;
}

function memberLabel(member: {
  userId: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  username?: string | null;
}): string {
  const display = usable(member.displayName);
  if (display) return display;
  const full = [usable(member.firstName), usable(member.lastName)].filter(Boolean).join(' ');
  if (full) return full;
  const email = usable(member.email);
  if (email) return email;
  const username = usable(member.username);
  if (username) return username;
  return 'Unnamed user';
}

const emptyIdp = (): CreateIdentityProviderDto => ({
  alias: '',
  displayName: '',
  providerType: 'azure-entra',
  tenantId: '',
  clientId: '',
  clientSecret: '',
  authorizationUrl: '',
  tokenUrl: '',
  userInfoUrl: '',
});

export function UsersPage() {
  const { tenantId, tenants, can, user } = useApp();
  const tenant = tenants.find((t) => t.id === tenantId);
  const [members, setMembers] = useState<MemberWithName[]>([]);
  const [groups, setGroups] = useState<AccessGroup[]>([]);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<MemberWithName | null>(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [managing, setManaging] = useState<MemberWithName | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [pendingDelete, setPendingDelete] = useState<MemberWithName | null>(null);
  const [newUser, setNewUser] = useState({
    email: '',
    username: '',
    firstName: '',
    lastName: '',
    password: '',
    temporary: true,
  });

  const [idps, setIdps] = useState<IdentityProvider[]>([]);
  const [showIdpSection, setShowIdpSection] = useState(false);
  const [addingIdp, setAddingIdp] = useState(false);
  const [idpLoading, setIdpLoading] = useState(false);
  const [idpError, setIdpError] = useState<string | null>(null);
  const [idpForm, setIdpForm] = useState<CreateIdentityProviderDto>(emptyIdp);
  const [rotatingSecretFor, setRotatingSecretFor] = useState<string | null>(null);
  const [newIdpSecret, setNewIdpSecret] = useState('');
  const [pendingIdp, setPendingIdp] = useState<IdentityProvider | null>(null);

  const loadGroups = useCallback(async () => {
    try {
      setGroups(await apiClient.listGroups());
    } catch {
      setGroups([]);
    }
  }, []);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const items = (await apiClient.listTenantMembers(tenantId)) as MemberWithName[];
      setMembers(items);
      setManaging((current) => (current ? items.find((m) => m.userId === current.userId) ?? current : current));
    } catch (err) {
      setError(errText(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
    await loadGroups();
  }, [tenantId, loadGroups]);

  const loadIdps = useCallback(async () => {
    if (!tenantId) return;
    try {
      setIdps(await apiClient.listIdentityProviders(tenantId));
    } catch {
      setIdps([]);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
    void loadIdps();
  }, [load, loadIdps]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => [memberLabel(m), m.email, m.identityProvider].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [members, search]);

  function memberGroups(userId: string): AccessGroup[] {
    return groups.filter((g) => g.members.some((m) => m.userId === userId));
  }

  function availableGroups(userId: string): AccessGroup[] {
    const taken = new Set(memberGroups(userId).map((g) => g.id));
    return groups.filter((g) => !taken.has(g.id));
  }

  function toggleAdd() {
    setError(null);
    setNewUser({ email: '', username: '', firstName: '', lastName: '', password: '', temporary: true });
    setAdding((v) => !v);
  }

  async function addMember() {
    if (!tenantId) return;
    setError(null);
    setLoading(true);
    try {
      await apiClient.addTenantMember(tenantId, {
        email: newUser.email.trim(),
        username: newUser.username.trim(),
        firstName: newUser.firstName.trim(),
        lastName: newUser.lastName.trim(),
        password: newUser.password,
        temporary: newUser.temporary,
      });
      setAdding(false);
      setNewUser({ email: '', username: '', firstName: '', lastName: '', password: '', temporary: true });
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to add user'));
    } finally {
      setLoading(false);
    }
  }

  async function removeMember(member: MemberWithName) {
    if (!tenantId) return;
    try {
      await apiClient.removeTenantMember(tenantId, member.userId);
    } catch (err) {
      setError(errText(err, 'Failed to delete user'));
    } finally {
      setPendingDelete(null);
      await load();
    }
  }

  function startEdit(member: MemberWithName) {
    setEditing(member);
    setEditFirstName(member.firstName ?? '');
    setEditLastName(member.lastName ?? '');
    setEditEmail(member.email ?? '');
  }

  async function saveEdit(member: MemberWithName) {
    if (!tenantId) return;
    setError(null);
    try {
      await apiClient.updateTenantMemberProfile(tenantId, member.userId, {
        ...(editFirstName.trim() ? { firstName: editFirstName.trim() } : {}),
        ...(editLastName.trim() ? { lastName: editLastName.trim() } : {}),
        email: editEmail.trim() || undefined,
      });
      setEditing(null);
      await load();
    } catch (err) {
      setError(errText(err, 'Failed to save name'));
      await load();
    }
  }

  async function addToGroup(member: MemberWithName) {
    if (!selectedGroupId) return;
    setError(null);
    try {
      await apiClient.addGroupMember(selectedGroupId, { userId: member.userId, email: member.email });
      setSelectedGroupId('');
      await loadGroups();
    } catch (err) {
      setError(errText(err, 'Failed to add user to group'));
    }
  }

  async function removeFromGroup(member: MemberWithName, group: AccessGroup) {
    setError(null);
    try {
      await apiClient.removeGroupMember(group.id, member.userId);
      await loadGroups();
    } catch (err) {
      setError(errText(err, 'Failed to remove user from group'));
    }
  }

  function canAddIdp(): boolean {
    if (!idpForm.alias || !idpForm.displayName || !idpForm.clientId || !idpForm.clientSecret) return false;
    if ((idpForm.providerType ?? 'azure-entra') === 'azure-entra') return !!idpForm.tenantId?.trim();
    return !!(idpForm.authorizationUrl && idpForm.tokenUrl && idpForm.userInfoUrl);
  }

  async function addIdp() {
    if (!tenantId) return;
    setIdpError(null);
    setIdpLoading(true);
    const providerType: IdpProviderType = idpForm.providerType ?? 'azure-entra';
    try {
      await apiClient.createIdentityProvider(tenantId, {
        alias: idpForm.alias.trim(),
        displayName: idpForm.displayName.trim(),
        providerType,
        tenantId: providerType === 'azure-entra' ? idpForm.tenantId?.trim() : undefined,
        clientId: idpForm.clientId.trim(),
        clientSecret: idpForm.clientSecret,
        authorizationUrl: providerType === 'oidc' ? idpForm.authorizationUrl?.trim() : undefined,
        tokenUrl: providerType === 'oidc' ? idpForm.tokenUrl?.trim() : undefined,
        userInfoUrl: providerType === 'oidc' ? idpForm.userInfoUrl?.trim() : undefined,
      });
      setAddingIdp(false);
      setIdpForm(emptyIdp());
      await loadIdps();
    } catch (err) {
      setIdpError(errText(err, 'Failed to configure identity provider'));
    } finally {
      setIdpLoading(false);
    }
  }

  async function deleteIdp(idp: IdentityProvider) {
    if (!tenantId) return;
    setIdpLoading(true);
    try {
      await apiClient.deleteIdentityProvider(tenantId, idp.alias);
      setPendingIdp(null);
      await loadIdps();
    } catch (err) {
      setIdpError(errText(err, 'Failed to remove identity provider'));
    } finally {
      setIdpLoading(false);
    }
  }

  async function rotateSecret(idp: IdentityProvider) {
    if (!tenantId || !newIdpSecret) return;
    setIdpLoading(true);
    setIdpError(null);
    try {
      await apiClient.updateIdentityProviderSecrets(tenantId, idp.alias, { clientSecret: newIdpSecret });
      setRotatingSecretFor(null);
      setNewIdpSecret('');
      await loadIdps();
    } catch (err) {
      setIdpError(errText(err, 'Failed to rotate secret'));
    } finally {
      setIdpLoading(false);
    }
  }

  if (!tenantId) {
    return (
      <div>
        <h1 className="text-2xl font-semibold">Users</h1>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <h2 className="mb-2 text-xl font-semibold">No tenant selected</h2>
          <p className="mb-4 max-w-md text-sm text-gray-500">
            Select a tenant from the header dropdown or go to the Tenants page to create one first.
          </p>
          <Link to="/tenants">
            <Button size="sm">Go to Tenants</Button>
          </Link>
        </div>
      </div>
    );
  }

  const idpType = idpForm.providerType ?? 'azure-entra';

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Users</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage users for <strong>{tenant?.name || 'current tenant'}</strong>. Access is the union of every group a user belongs to. Tick pages on{' '}
            <Link to="/groups" className="text-blue-600">Groups</Link>.
          </p>
        </div>
        <Button size="sm" onClick={toggleAdd}>Add user</Button>
      </div>

      {error ? (
        <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">{error}</div>
      ) : null}

      {adding ? (
        <Card className="mt-4">
          <CardContent className="pt-6">
            <h2 className="mb-2 text-lg font-semibold">Add user to {tenant?.name}</h2>
            <div className="grid max-w-xl gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1 text-sm">
                  <span>First name</span>
                  <Input placeholder="John" value={newUser.firstName} onChange={(e) => setNewUser((u) => ({ ...u, firstName: e.target.value }))} />
                </label>
                <label className="grid gap-1 text-sm">
                  <span>Last name</span>
                  <Input placeholder="Doe" value={newUser.lastName} onChange={(e) => setNewUser((u) => ({ ...u, lastName: e.target.value }))} />
                </label>
              </div>
              <label className="grid gap-1 text-sm">
                <span>Username</span>
                <Input placeholder="jdoe" value={newUser.username} onChange={(e) => setNewUser((u) => ({ ...u, username: e.target.value }))} />
              </label>
              <label className="grid gap-1 text-sm">
                <span>Email</span>
                <Input type="email" placeholder="john@example.com" value={newUser.email} onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))} />
              </label>
              <label className="grid gap-1 text-sm">
                <span>Password</span>
                <Input type="password" placeholder="Enter a password" value={newUser.password} onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))} />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="h-4 w-4" checked={newUser.temporary} onChange={() => setNewUser((u) => ({ ...u, temporary: !u.temporary }))} />
                Temporary. User must change password on first login
              </label>
              <p className="text-xs text-gray-500">New users start in the Viewer group. Assign more groups after they are created.</p>
              <div className="flex gap-2">
                <Button size="sm" disabled={loading} onClick={() => void addMember()}>Add</Button>
                <Button size="sm" variant="ghost" onClick={toggleAdd}>Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="mt-4">
        <Input placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((m) => (
          <Card key={m.userId}>
            <CardContent className="grid gap-3 pt-6">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="truncate text-base font-semibold">{memberLabel(m)}</h2>
                  <p className="truncate text-sm text-gray-500">{m.email || '-'}</p>
                  {m.identityProvider ? <Badge variant="secondary" className="mt-1">SSO · {m.identityProvider}</Badge> : null}
                </div>
                <DropdownMenu
                  trigger={<Button size="icon" variant="ghost" aria-label="User actions"><MoreVertical className="h-4 w-4" /></Button>}
                  items={[
                    { label: 'Edit user', onClick: () => startEdit(m) },
                    ...(can('groups', 'manage_members') ? [{ label: 'Manage groups', onClick: () => { setSelectedGroupId(''); setManaging(m); } }] : []),
                    {
                      label: 'Delete user',
                      className: 'text-red-600',
                      onClick: () => {
                        if (m.userId !== user.userId) setPendingDelete(m);
                      },
                    },
                  ]}
                />
              </div>
              <div>
                <h3 className="mb-2 text-sm font-medium">Group memberships</h3>
                {memberGroups(m.userId).length ? (
                  <div className="flex flex-wrap gap-2">
                    {memberGroups(m.userId).map((g) => (
                      <Badge key={g.id} variant="outline">{g.name}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No group memberships</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {!loading && filtered.length === 0 ? (
          <Card className="md:col-span-2 lg:col-span-3">
            <CardContent className="py-8 text-center text-sm text-gray-500">
              No members yet. Add the first user above.
            </CardContent>
          </Card>
        ) : null}
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent maxWidth="md">
          <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
          <DialogBody>
            <div className="grid gap-3">
              <label className="grid gap-1 text-sm"><span className="text-xs text-gray-500">First name</span><Input value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} /></label>
              <label className="grid gap-1 text-sm"><span className="text-xs text-gray-500">Last name</span><Input value={editLastName} onChange={(e) => setEditLastName(e.target.value)} /></label>
              <label className="grid gap-1 text-sm"><span className="text-xs text-gray-500">Email</span><Input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} /></label>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button size="sm" onClick={() => editing && void saveEdit(editing)}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!managing} onOpenChange={(open) => { if (!open) setManaging(null); }}>
        <DialogContent maxWidth="md">
          <DialogHeader>
            <DialogTitle>Manage groups</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {managing ? (
              <div className="grid gap-4">
                <p className="text-sm text-gray-500">{managing.email || memberLabel(managing)}</p>
                <div>
                  <span className="text-xs text-gray-500">Add user to group</span>
                  <div className="mt-1 flex gap-2">
                    <select className={selectClass} value={selectedGroupId} onChange={(e) => setSelectedGroupId(e.target.value)}>
                      <option value="">Select a group...</option>
                      {availableGroups(managing.userId).map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                    <Button size="sm" disabled={!selectedGroupId} onClick={() => void addToGroup(managing)}>Add</Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {memberGroups(managing.userId).map((g) => (
                    <Badge key={g.id} variant="outline">
                      {g.name}
                      <button type="button" className="ml-1" aria-label={`Remove from ${g.name}`} onClick={() => void removeFromGroup(managing, g)}>×</button>
                    </Badge>
                  ))}
                  {memberGroups(managing.userId).length === 0 ? <span className="text-sm text-gray-500">No group memberships</span> : null}
                </div>
              </div>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button size="sm" onClick={() => setManaging(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
        <DialogContent maxWidth="md">
          <DialogHeader><DialogTitle>Delete user</DialogTitle></DialogHeader>
          <DialogBody>
            <p className="text-sm">
              Delete {pendingDelete ? memberLabel(pendingDelete) : ''}? They will be removed from this tenant and all of its groups.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => pendingDelete && void removeMember(pendingDelete)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">Single Sign-On</h2>
            <p className="text-sm text-gray-500">
              Allow users to sign in with an external identity provider such as Microsoft Entra ID, Okta, or Google.
            </p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setShowIdpSection((v) => {
                const next = !v;
                if (next) void loadIdps();
                return next;
              });
            }}
          >
            {showIdpSection ? 'Hide' : 'Configure'}
          </Button>
        </div>
        {showIdpSection ? (
          <div className="mt-4 space-y-4">
            {idpError ? (
              <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">{idpError}</div>
            ) : null}
            {idps.map((idp) => (
              <Card key={idp.alias}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="font-medium">{idp.displayName}</span>
                      <span className="ml-2 text-xs text-gray-500">({idp.alias})</span>
                      <div className="mt-1 text-xs text-gray-500">
                        Provider: {idp.providerId} · {idp.enabled ? <span className="text-green-600">Enabled</span> : <span className="text-amber-600">Disabled</span>} · Client ID: {idp.config['clientId'] || '-'}
                      </div>
                      {idp.secretRef ? <div className="mt-0.5 font-mono text-xs text-gray-400">Secret: {idp.secretRef}</div> : null}
                      {idp.brokerRedirectUri ? (
                        <div className="mt-1 text-xs text-gray-500">Redirect URI: <code className="break-all">{idp.brokerRedirectUri}</code></div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1">
                      {rotatingSecretFor !== idp.alias ? (
                        <Button size="sm" variant="ghost" onClick={() => { setRotatingSecretFor(idp.alias); setNewIdpSecret(''); setIdpError(null); }}>
                          Rotate secret
                        </Button>
                      ) : null}
                      <Button size="sm" variant="ghost" className="text-red-600" disabled={idpLoading} onClick={() => setPendingIdp(idp)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                  {rotatingSecretFor === idp.alias ? (
                    <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-700">
                      <label className="grid max-w-md gap-1.5 text-xs text-gray-500">
                        New client secret. Stored in Vault, never shown again
                        <div className="flex gap-2">
                          <Input type="password" autoComplete="new-password" placeholder="Enter new client secret" value={newIdpSecret} onChange={(e) => setNewIdpSecret(e.target.value)} />
                          <Button size="sm" disabled={!newIdpSecret || idpLoading} onClick={() => void rotateSecret(idp)}>
                            {idpLoading ? 'Rotating...' : 'Rotate'}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setRotatingSecretFor(null); setNewIdpSecret(''); }}>Cancel</Button>
                        </div>
                      </label>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ))}
            {idps.length === 0 && !addingIdp ? (
              <p className="text-sm text-gray-500">No identity providers configured yet. Add one to enable external sign-in.</p>
            ) : null}
            {addingIdp ? (
              <Card>
                <CardContent className="pt-6">
                  <h3 className="mb-2 font-semibold">Add Identity Provider</h3>
                  <p className="mb-3 text-xs text-gray-500">
                    New users who sign in via this provider are added to the <strong>Viewer</strong> group and appear on this page. Assign extra groups afterwards.
                  </p>
                  <div className="grid max-w-xl gap-3">
                    <label className="grid gap-1 text-sm">
                      <span>Provider type</span>
                      <select className={selectClass} value={idpType} onChange={(e) => setIdpForm((f) => ({ ...f, providerType: e.target.value as IdpProviderType }))}>
                        <option value="azure-entra">Azure Entra ID</option>
                        <option value="oidc">Generic OIDC</option>
                      </select>
                    </label>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="grid gap-1 text-sm">
                        <span>Alias (machine name)</span>
                        <Input placeholder="entra-id" value={idpForm.alias} onChange={(e) => setIdpForm((f) => ({ ...f, alias: e.target.value }))} />
                      </label>
                      <label className="grid gap-1 text-sm">
                        <span>Display name</span>
                        <Input placeholder="Microsoft Entra ID" value={idpForm.displayName} onChange={(e) => setIdpForm((f) => ({ ...f, displayName: e.target.value }))} />
                      </label>
                    </div>
                    {idpType === 'azure-entra' ? (
                      <label className="grid gap-1 text-sm">
                        <span>Azure tenant ID</span>
                        <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={idpForm.tenantId ?? ''} onChange={(e) => setIdpForm((f) => ({ ...f, tenantId: e.target.value }))} />
                      </label>
                    ) : null}
                    <label className="grid gap-1 text-sm">
                      <span>Client ID</span>
                      <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={idpForm.clientId} onChange={(e) => setIdpForm((f) => ({ ...f, clientId: e.target.value }))} />
                    </label>
                    <label className="grid gap-1 text-sm">
                      <span>Client Secret</span>
                      <Input type="password" autoComplete="new-password" placeholder="Enter client secret" value={idpForm.clientSecret} onChange={(e) => setIdpForm((f) => ({ ...f, clientSecret: e.target.value }))} />
                    </label>
                    {idpType === 'oidc' ? (
                      <>
                        <label className="grid gap-1 text-sm"><span>Authorization URL</span><Input value={idpForm.authorizationUrl ?? ''} onChange={(e) => setIdpForm((f) => ({ ...f, authorizationUrl: e.target.value }))} /></label>
                        <label className="grid gap-1 text-sm"><span>Token URL</span><Input value={idpForm.tokenUrl ?? ''} onChange={(e) => setIdpForm((f) => ({ ...f, tokenUrl: e.target.value }))} /></label>
                        <label className="grid gap-1 text-sm"><span>User Info URL</span><Input value={idpForm.userInfoUrl ?? ''} onChange={(e) => setIdpForm((f) => ({ ...f, userInfoUrl: e.target.value }))} /></label>
                      </>
                    ) : null}
                    <div className="flex gap-2">
                      <Button disabled={!canAddIdp() || idpLoading} onClick={() => void addIdp()}>
                        {idpLoading ? 'Saving...' : 'Configure Provider'}
                      </Button>
                      <Button variant="ghost" onClick={() => { setAddingIdp(false); setIdpError(null); setIdpForm(emptyIdp()); }}>Cancel</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Button size="sm" variant="outline" onClick={() => { setIdpError(null); setIdpForm(emptyIdp()); setAddingIdp(true); }}>
                Add Identity Provider
              </Button>
            )}
            <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-gray-800">
              <p className="mb-1 font-medium">How it works</p>
              <ol className="list-inside list-decimal space-y-0.5">
                <li>Register an OIDC application in your identity provider (Entra ID, Okta, Google, etc.)</li>
                <li>For Azure Entra, paste the tenant ID, client ID, and client secret. Endpoints are filled in automatically.</li>
                <li>Register the Keycloak broker redirect URI shown after save on the Entra app</li>
                <li>OrqForge configures the tenant&apos;s Keycloak realm automatically with the IdP</li>
                <li>Users will see a sign-in button on the Keycloak login page for this provider</li>
                <li>On first sign-in, Keycloak creates the user and adds them to the Viewer group</li>
                <li>Assign extra groups from the user card. Access is the union of every group.</li>
                <li>To rotate the client secret, use Rotate secret and enter a new value</li>
              </ol>
            </div>
          </div>
        ) : null}
      </div>

      <Dialog open={!!pendingIdp} onOpenChange={(open) => { if (!open) setPendingIdp(null); }}>
        <DialogContent maxWidth="md">
          <DialogHeader><DialogTitle>Remove identity provider</DialogTitle></DialogHeader>
          <DialogBody>
            <p className="text-sm">
              Remove identity provider &quot;{pendingIdp?.displayName}&quot;? Users will no longer be able to sign in via this provider.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingIdp(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => pendingIdp && void deleteIdp(pendingIdp)}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
